import { TrainingInitQuery } from '~/data';
import {
  Chapter,
  CompanyContact,
  Policy,
  Sequence
} from './navigation/NavigationSlice';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { createSupportedLanguages } from './settings/createSupportedLanguages';
import { t } from 'ttag';
import { markAppReload } from '~/lib/reloadDiagnostics';
import { applyBrandColors } from '~/lib/themes/applyBrandColors';
import { isForceDarkTheme, isForceLightTheme } from '~/lib/themes/forceTheme';
import { toPretest } from './pretest/pretest';

const resolveSurfaceIsDark = (darkMode: string | null | undefined) => {
  if (isForceDarkTheme()) return true;
  if (isForceLightTheme()) return false;
  if (darkMode === 'dark') return true;
  if (darkMode === 'automatic') {
    return (
      window?.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false
    );
  }
  return false;
};

/**
 * Utility function to process the GraphQL data into something a little more usable by the UI.
 * In particular, it flattens the tree of chapters and sequences into an array.
 * @param lt LearnerTraining from the GraphQL query
 * @returns Zustand store data
 */
export function processData(data: TrainingInitQuery) {
  const items: Array<Sequence | Chapter> = [];
  const { learnerTraining: lt, me: user } = data;
  let highestUnlockedPosition = -1;

  if (!lt.training.chapters) {
    useToastStore.getState().add({
      severity: ToastSeverity.Error,
      message: t`An unexpected error occurred.`
    });
  }

  lt.training.chapters?.forEach((chapter, indexPosition) => {
    // Add each chapter to the list of items
    items.push({
      kind: 'chapter',
      id: chapter.id,
      title: chapter.title,
      duration: chapter.duration,
      position: (indexPosition + 1) * 10000,
      sequenceCount: chapter.chapterSequences.length,
      testedOut: chapter.testedOut ?? false
    });

    // Add each sequence of the chapter to the same list
    chapter.chapterSequences.forEach(
      ({
        id: chapterSequenceId,
        position,
        sequenceId,
        title,
        duration,
        type,
        icon
      }) => {
        const item = {
          kind: 'sequence',
          id: sequenceId,
          position: (indexPosition + 1) * 10000 + (position || 0),
          chapterId: chapter.id,
          chapterSequenceId,
          duration: duration ? Math.round(duration / 6) / 10 : 0,
          type,
          title: title || '',
          icon: icon || ''
        };
        items.push(item as Sequence);

        if (item.chapterSequenceId === lt.chapterSequenceId) {
          // This is how we get the position of the last visited item
          highestUnlockedPosition = item.position;
        }
      }
    );
  });

  if (user.company) {
    // CAT-1261: keep brand hex as-is; derive on-accent / focus / link tokens
    // so near-white (or near-black in dark mode) primary stays legible.
    applyBrandColors(document.documentElement, {
      brandColorDark: user.company.brandColorDark,
      brandColorLight: user.company.brandColorLight,
      surfaceIsDark: resolveSurfaceIsDark(data.learnerTraining.darkMode)
    });
  }

  //we can't continue if we don't have chapterSequenceId
  if (lt.chapterSequenceId === '') {
    markAppReload('empty-chapterSequenceId', { learnerTrainingId: lt.id });
    document.location.reload();
  }

  return {
    // Navigation
    learnerTrainingId: lt.id,
    chapterSequenceId: lt.chapterSequenceId || '',
    title: lt.training.title,
    duration: lt.training.duration || 0,
    regionName: lt.training.region?.name || '',
    highestUnlockedPosition,
    items: items.sort((a, b) => a.position - b.position),
    status: lt.status,
    skip: lt.status === 'completed' ? true : lt.skip || false,
    player: lt.player || 'standard',
    progress: lt.progress || 0,
    scorePercent: lt.scorePercent ?? 0,
    lastAttemptScorePercent: lt.lastAttemptScorePercent ?? null,
    scorePassingPercent: lt.scorePassingPercent || 0,
    attemptStatus: lt.attemptStatus || '',
    attemptLimit: lt.attemptLimit ?? null,
    attemptsRemaining: lt.attemptsRemaining ?? null,
    pretest: toPretest(lt.pretest),
    policies: lt.policies as Array<Policy>,
    policiesForSignature: lt.policiesForSignature as Array<Policy>,
    policyAcknowledgement: lt.policyAcknowledgement || false,
    policyAcknowledgementSignature: lt.policyAcknowledgementSignature || false,
    companyContacts: lt.companyContacts as Array<CompanyContact>,

    auditoryFeedback: !!data.learnerTraining.auditoryFeedback,
    visualFeedback: !!data.learnerTraining.visualFeedback,

    certificateUrl: data.learnerTraining.certificateUrl || '',
    learnerPortalUrl: data.learnerTraining.learnerPortalUrl || '',

    // Settings
    durationEstimated: lt.training.durationEstimated ?? false,
    textLocale: lt.textLocale || '',
    textLocales: lt.training.textLocales || [],
    subtitleLocale: lt.subtitleLocale || '',
    subtitleLocales: lt.training.subtitleLocales || [],
    supportedLanguages: createSupportedLanguages({
      textLocales: lt?.training?.textLocales || [],
      subtitleLocales: lt?.training?.subtitleLocales || [],
      mediumLocales: lt?.training?.mediumLocales || []
    }),
    languageAutoDetect: lt.training.languageAutoDetect || false,
    askName: data.learnerTraining.askName || false,
    autoPlay: data.learnerTraining.autoPlay || false,
    playbackSpeed: data.learnerTraining.playbackSpeed || 1,
    featureFocus: data.learnerTraining.featureFocus || false,
    fasterPlaybackSpeed: data.learnerTraining.fasterPlaybackSpeed || false,
    featureShowHotline: !!data.learnerTraining.featureShowHotline,
    featureShowReviewStep: !!data.learnerTraining.featureShowReviewStep,
    featureShowCertificate: !!data.learnerTraining.featureShowCertificate,
    featureShowSocialShare: !!data.learnerTraining.featureShowSocialShare,
    featureShowAbout: !!data.learnerTraining.featureShowAbout,
    featureShowLlama: !!data.learnerTraining.featureShowLlama,
    featureShowChatbot: !!data.learnerTraining.featureShowChatbot,
    featureShowNextTraining: !!data.learnerTraining.featureShowNextTraining,
    mediumLocale: lt.mediumLocale || '',
    mediumLocales: lt.training.mediumLocales || [],
    subtitles: lt.subtitles || false,
    featureShowIssueReporting: !!data.learnerTraining.featureShowIssueReporting,
    darkMode: data.learnerTraining.darkMode || 'automatic',
    onboarding: data.learnerTraining.onboarding,
    shouldDisplayCourseDurationCard:
      data.learnerTraining.shouldDisplayCourseDurationCard || false,
    shouldDisplayTimeRemainingPill:
      data.learnerTraining.shouldDisplayTimeRemainingPill || false,
    remainingDurationInMinutesForCourseTimers:
      data.learnerTraining.remainingDurationInMinutesForCourseTimers ?? null,
    remainingDurationInSecondsForCourseTimers:
      data.learnerTraining.remainingDurationInSecondsForCourseTimers ?? null,
    chatMessages: data.learnerTraining.chatbotMessages,
    chatGreeting: data.learnerTraining.chatGreeting || '',
    chatQuestions: data.learnerTraining.chatQuestions || [],
    featureShowHomeButton: data.learnerTraining.featureShowHomeButton || true,
    showDemoBanner: data.learnerTraining.showDemoBanner || false,
    shouldSaveVideoWatchedSeconds:
      data.learnerTraining.shouldSaveVideoWatchedSeconds || false,
    autoSaveVideoWatchedSecondsIntervalInSeconds:
      data.learnerTraining.autoSaveVideoWatchedSecondsIntervalInSeconds || null,

    // User
    promptOptions: data.learnerTraining.promptOptions || [],
    promptProfile: data.learnerTraining.promptProfile || false,
    firstName: user.firstName || '',
    companyName: user.company.name || '',
    companyId: user.company.id || '',
    logoUrl: user.company.logoUrl || '',
    feedbackUrl: user.company.feedbackUrl || '',
    disclaimer: lt.disclaimer || undefined,

    languages: []
  };
}
